import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const DEPTH_LAYERS = 36;
const DEPTH_SPACING = 3.2;
const MARK_HUE = "235deg";

function Mark3D() {
  const markRef = useRef<HTMLDivElement>(null);
  const auraRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let yaw = 0, pitch = 0;
    let raf = 0;

    const onMove = (e: MouseEvent) => { mouseX = e.clientX; mouseY = e.clientY; };
    const onLeave = () => { mouseX = window.innerWidth / 2; mouseY = window.innerHeight / 2; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    const tick = () => {
      const mark = markRef.current;
      if (!mark) { raf = requestAnimationFrame(tick); return; }
      const rect = mark.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const nx = (mouseX - cx) / (window.innerWidth * 0.5);
      const ny = (mouseY - cy) / (window.innerHeight * 0.5);
      const tYaw = Math.max(-1, Math.min(1, nx)) * 32;
      const tPitch = -Math.max(-1, Math.min(1, ny)) * 24;
      yaw = lerp(yaw, tYaw, 0.1);
      pitch = lerp(pitch, tPitch, 0.1);
      const t = performance.now() * 0.001;
      const floatY = Math.sin(t * 0.6) * 6;
      const floatRoll = Math.sin(t * 0.4) * 1.4;
      mark.style.transform = `translate3d(0, ${floatY}px, 0) rotateX(${pitch}deg) rotateY(${yaw}deg) rotateZ(${floatRoll}deg)`;
      if (auraRef.current) {
        auraRef.current.style.transform = `translate3d(${yaw * 1.6}px, ${pitch * -1.2}px, -120px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const layers = Array.from({ length: DEPTH_LAYERS }, (_, i) => {
    const z = (i - (DEPTH_LAYERS - 1) / 2) * DEPTH_SPACING;
    const isTop = i === DEPTH_LAYERS - 1;
    const isBack = i === 0;
    let filter: string | undefined;
    if (!isTop && !isBack) {
      const t = i / (DEPTH_LAYERS - 1);
      const brightness = 55 + t * 55;
      const saturate = 1000 + t * 200;
      const invert = 28 + t * 46;
      filter = `brightness(0) saturate(100%) invert(${invert}%) sepia(22%) saturate(${saturate}%) hue-rotate(${MARK_HUE}) brightness(${brightness}%)`;
    }
    return (
      <div
        key={i}
        className={`dyna-hero__mark3d-slice${isTop ? " dyna-hero__mark3d-slice--top" : ""}${isBack ? " dyna-hero__mark3d-slice--back" : ""}`}
        style={{ transform: `translate3d(0, 0, ${z}px)`, zIndex: i, filter }}
      />
    );
  });

  return (
    <div className="dyna-hero__mark3d" aria-hidden>
      <div className="dyna-hero__mark3d-aura" ref={auraRef} />
      <div className="dyna-hero__mark3d-extrude" ref={markRef} style={{ transformStyle: "preserve-3d" }}>
        {layers}
      </div>
    </div>
  );
}

interface HeroSectionProps {
  onCTA: () => void;
}

export default function HeroSection({ onCTA }: HeroSectionProps) {
  const { t } = useTranslation();

  return (
    <section className="dyna-hero" id="top">
      <div className="dyna-hero__video-scrim" />

      <div className="dyna-hero__stage">
        <div className="dyna-hero__content">
          <p className="dyna-hero__lead">{t("dyna.hero.lead")}</p>
          <h1 className="dyna-hero__title">
            {t("dyna.hero.title_1")}<br />
            {t("dyna.hero.title_2")}<br />
            <em>{t("dyna.hero.title_em")}</em><br />
            {t("dyna.hero.title_3")}<br />
            {t("dyna.hero.title_4")}
          </h1>

          <p className="dyna-hero__sub">{t("dyna.hero.sub")}</p>

          <div className="dyna-hero__ctas">
            <button className="dyna-btn dyna-btn--primary dyna-btn--xl" onClick={onCTA}>
              {t("dyna.hero.cta_primary")} <span className="dyna-arrow">→</span>
            </button>
            <a href="#work" className="dyna-btn dyna-btn--secondary dyna-btn--xl">
              {t("dyna.hero.cta_secondary")}
            </a>
          </div>
        </div>

        <Mark3D />
      </div>

    </section>
  );
}
